import { View, Text, ScrollView, Pressable, TextInput } from 'react-native';
import React, { useEffect, useMemo, useState } from 'react';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { enableGiving, disableGiving, updateGivingAppearance, updateGivingFeeChoice } from '@/data/giving';
import { showAlert } from '@/utils/showAlert';
import Toggle from '@/components/General/inputs/Toggle';
import ToggleRow from '@/components/General/inputs/ToggleRow';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import { Table } from '@/components/General/Table';
import GivingCard from '@/components/Giving/GivingCard';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Clipboard from '@react-native-clipboard/clipboard';
import Toast from 'react-native-toast-message';
import ColorPicker, { HueSlider, Panel1 } from 'reanimated-color-picker';
import SelectInput from '@/components/General/inputs/SelectInput';
import SegmentedTabs from '@/components/General/SegmentedTabs';
import { getAccessibleTextColor, getGivingColorPalette, mixHexColors } from '@/utils/colorAccessibility';

const formatCurrency = (cents) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(cents / 100);

const STATUS_BADGE_STYLES = {
    ACTIVE: { bg: 'givingContainer', text: 'giving' },
    PAUSED: { bg: 'warningContainer', text: 'warning' },
    CANCELLED: { bg: 'errorContainer', text: 'error' },
};

const HEX_COLOR = /^#[0-9A-F]{6}$/i;

function ColorSetting({ title, value, onChange, testID, expanded, onToggle }) {
    const valid = HEX_COLOR.test(value);

    return (
        <View style={styles.colorSetting} testID={testID}>
            <Text style={styles.inputLabel}>{title}</Text>
            <View style={styles.colorInputRow}>
                <Pressable
                    testID={`${testID}-swatch`}
                    accessibilityRole="button"
                    accessibilityLabel={`Choose ${title.toLowerCase()}`}
                    accessibilityState={{ expanded }}
                    onPress={onToggle}
                    style={styles.colorSwatch(valid ? value : 'transparent')}
                />
                <TextInput
                    testID={`${testID}-input`}
                    value={value}
                    onChangeText={(text) => onChange(text.toUpperCase())}
                    autoCapitalize="characters"
                    autoCorrect={false}
                    maxLength={7}
                    placeholder="#000000"
                    accessibilityLabel={title}
                    style={[styles.colorTextInput, !valid && styles.invalidColorInput]}
                />
            </View>
            {expanded && (
                <ColorPicker value={valid ? value : '#000000'} onChangeJS={({ hex }) => onChange(hex.slice(0, 7).toUpperCase())} style={styles.colorPicker}>
                    <Panel1 style={styles.colorPanel} />
                    <HueSlider style={styles.hueSlider} />
                </ColorPicker>
            )}
        </View>
    );
}

export default function GivingSettingsScreen() {
    const router = useRouter();
    const queryClient = useQueryClient();
    const { theme: { colors, fonts } } = useUnistyles();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: organization, isFetching: loadingOrganization } = Query.organization.byId({ id: loggedInUser?.organizationID });
    const { data: givingMerchant } = Query.giving.merchant({ organizationID: loggedInUser?.organizationID });
    const { data: orgRecurringGifts, isFetching: loadingRecurring } = Query.giving.orgRecurringGifts({ organizationID: loggedInUser?.organizationID });
    const { data: funds } = Query.contributionFunds.all();

    const organizationID = organization?.id;
    const enabled = !!organization?.enableGiving;
    const defaultAppearance = useMemo(() => ({
        baseColor: colors.giving,
        backgroundColor: colors.background,
        surfaceColor: colors.surface,
        textColor: colors.text,
        logoPosition: 'square',
        logoStyle: 'light',
    }), [colors]);
    const [appearance, setAppearance] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [expandedColor, setExpandedColor] = useState(null);
    const currentAppearance = appearance || defaultAppearance;

    useEffect(() => {
        if (!givingMerchant) return;
        setAppearance({ ...defaultAppearance, ...(givingMerchant.appearance || {}) });
    }, [givingMerchant, defaultAppearance]);

    const enableMutation = useMutation({
        mutationFn: () => enableGiving(organizationID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.merchant(organizationID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.organization.byId(organizationID) });
            router.push('/settings/organization/giving/onboarding');
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to enable online giving' });
        },
    });

    const disableMutation = useMutation({
        mutationFn: () => disableGiving(organizationID),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.merchant(organizationID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.organization.byId(organizationID) });
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to disable online giving' });
        },
    });

    const feeChoiceMutation = useMutation({
        mutationFn: (allowed) => updateGivingFeeChoice({ organizationID, allowed }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.organization.byId(organizationID) }),
        onError: (error) => Toast.show({ type: 'error', text2: error.message }),
    });

    const appearanceMutation = useMutation({
        mutationFn: () => updateGivingAppearance({ organizationID, appearance: currentAppearance }),
        onSuccess: (savedAppearance) => {
            setAppearance({ ...defaultAppearance, ...savedAppearance });
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.merchant(organizationID) });
            Toast.show({ type: 'success', text2: 'Giving page appearance saved' });
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to save giving page appearance' });
        },
    });

    const updateAppearance = (key, value) => setAppearance((current) => ({
        ...(current || defaultAppearance),
        [key]: value,
        ...(key === 'baseColor' ? getGivingColorPalette(value) : null),
    }));
    const appearanceIsValid = ['baseColor', 'backgroundColor', 'surfaceColor', 'textColor']
        .every((key) => HEX_COLOR.test(currentAppearance[key]));

    const handleToggle = (value) => {
        if (!organizationID) return;
        if (value) {
            showAlert({
                title: 'Enable Online Giving?',
                subtitle: 'This will start onboarding so your organization can accept online gifts.',
                confirmText: 'Enable',
                onConfirm: () => enableMutation.mutate(),
            });
        } else {
            disableMutation.mutate();
        }
    };

    const handleCopyUrl = () => {
        const url = `${process.env.EXPO_PUBLIC_APP_WEB_URL || 'https://app.reachchms.com'}/give/${organization?.handle}`;
        Clipboard.setString(url);
        Toast.show({ type: 'success', text2: 'Public giving URL copied' });
    };

    const isProcessing = loadingOrganization || enableMutation.isPending || disableMutation.isPending;
    const isActive = givingMerchant?.merchantStatus === 'ACTIVE';
    const applicationStatus = (givingMerchant?.applicationStatus || givingMerchant?.merchantStatus || '').toUpperCase();
    const canCustomizeAppearance = isActive && ['COMPLETED', 'ACTIVE'].includes(applicationStatus);

    const recurringColumns = useMemo(() => [
        {
            value: 'Giver',
            width: 160,
            renderer: (item) => {
                const name = item.person ? `${item.person.firstName || ''} ${item.person.lastName || ''}`.trim() : 'Unknown';
                return (
                    <Text style={{ fontFamily: fonts.bold, fontSize: 13, color: colors.text }} numberOfLines={1}>
                        {name}
                    </Text>
                );
            },
        },
        {
            value: 'Amount',
            width: 120,
            renderer: (item) => (
                <View>
                    <Text style={{ fontFamily: fonts.bold, fontSize: 13, color: colors.giving }}>
                        {formatCurrency(item.amount)}
                    </Text>
                    <Text style={{ fontFamily: fonts.body, fontSize: 11, color: colors.textSecondary, textTransform: 'capitalize' }}>
                        {item.frequency?.toLowerCase()}
                    </Text>
                </View>
            ),
        },
        {
            value: 'Funds',
            width: 180,
            renderer: (item) => {
                const splits = (item.fundSplits || []).map((split) => {
                    const fund = (funds || []).find((f) => f.id === split.fundID);
                    return { name: fund?.name || 'Unknown fund', amount: formatCurrency(split.amount) };
                });
                if (splits.length === 0) return <Text style={{ color: colors.textSecondary, fontSize: 12 }}>—</Text>;
                return (
                    <View style={{ gap: 2 }}>
                        {splits.map((s, i) => (
                            <View key={i} style={{ flexDirection: 'row', gap: 4 }}>
                                <Text style={{ color: colors.text, fontSize: 12 }} numberOfLines={1}>{s.name}</Text>
                                <Text style={{ color: colors.textSecondary, fontSize: 11 }}>{s.amount}</Text>
                            </View>
                        ))}
                    </View>
                );
            },
        },
        {
            value: 'Next Run',
            width: 110,
            renderer: (item) => (
                <Text style={{ fontFamily: fonts.body, fontSize: 12, color: colors.textSecondary }}>
                    {item.nextRunAt ? new Date(item.nextRunAt).toLocaleDateString() : '—'}
                </Text>
            ),
        },
        {
            value: 'Status',
            width: 100,
            renderer: (item) => {
                const badge = STATUS_BADGE_STYLES[item.status] || STATUS_BADGE_STYLES.ACTIVE;
                return (
                    <View style={[styles.statusBadge, { backgroundColor: colors[badge.bg] }]}>
                        <Text style={[styles.statusText, { color: colors[badge.text] }]}>
                            {item.status}
                        </Text>
                    </View>
                );
            },
        },
        {
            value: 'Method',
            width: 130,
            renderer: (item) => {
                const pm = item.paymentMethod;
                if (!pm) return <Text style={{ color: colors.textSecondary, fontSize: 12 }}>—</Text>;
                if (pm.methodType === 'APPLE_PAY') {
                    return (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Text style={{ color: colors.text, fontSize: 12, textTransform: 'capitalize' }} numberOfLines={1}>
                                Apple Pay {pm.cardBrand || 'Card'}
                            </Text>
                            <Text style={{ color: colors.textSecondary, fontSize: 11 }}>•{pm.cardLast4}</Text>
                        </View>
                    );
                }
                if (pm.methodType === 'CARD') {
                    return (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Text style={{ color: colors.text, fontSize: 12, textTransform: 'capitalize' }} numberOfLines={1}>
                                {pm.cardBrand || 'Card'}
                            </Text>
                            <Text style={{ color: colors.textSecondary, fontSize: 11 }}>•{pm.cardLast4}</Text>
                        </View>
                    );
                }
                if (pm.methodType === 'ACH') {
                    return (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                            <Text style={{ color: colors.text, fontSize: 12 }} numberOfLines={1}>ACH</Text>
                            <Text style={{ color: colors.textSecondary, fontSize: 11 }}>•{pm.achAccountNumberLast4}</Text>
                        </View>
                    );
                }
                return <Text style={{ color: colors.textSecondary, fontSize: 12 }}>{pm.methodType || '—'}</Text>;
            },
        },
    ], [colors, fonts, funds]);

    const recurringActions = useMemo(() => [
        {
            name: 'View person',
            icon: { web: 'person-outline' },
            onSelect: (item) => {
                if (item.personID) router.push(`/people/${item.personID}`);
            },
        },
    ], [router]);

    const tabs = [
        { key: 'overview', label: 'Overview' },
        ...(canCustomizeAppearance ? [{ key: 'appearance', label: 'Appearance' }] : []),
        ...(isActive ? [{ key: 'recurring', label: 'Recurring' }] : []),
    ];
    const selectedTab = tabs.some((tab) => tab.key === activeTab) ? activeTab : 'overview';
    const merchantStatus = givingMerchant?.applicationStatus || givingMerchant?.merchantStatus || 'Not started';
    const statusReady = enabled && canCustomizeAppearance;
    const preview = {
        baseColor: HEX_COLOR.test(currentAppearance.baseColor) ? currentAppearance.baseColor : colors.giving,
        backgroundColor: HEX_COLOR.test(currentAppearance.backgroundColor) ? currentAppearance.backgroundColor : colors.background,
        surfaceColor: HEX_COLOR.test(currentAppearance.surfaceColor) ? currentAppearance.surfaceColor : colors.surface,
        textColor: HEX_COLOR.test(currentAppearance.textColor) ? currentAppearance.textColor : colors.text,
    };
    const previewGradient = [mixHexColors(preview.baseColor, '#FFFFFF', 0.04), mixHexColors(preview.baseColor, '#000000', 0.2)];
    const previewOnBase = getAccessibleTextColor(mixHexColors(preview.baseColor, '#000000', 0.1));
    const previewInputColor = mixHexColors(preview.surfaceColor, preview.textColor, 0.07);
    const previewLogoUrl = currentAppearance.logoPosition === 'long'
        ? (currentAppearance.logoStyle === 'dark'
            ? (organization?.logoPublicUrlLongDark || organization?.logoPublicUrlLong)
            : (organization?.logoPublicUrlLong || organization?.logoPublicUrlLongDark))
        : (currentAppearance.logoStyle === 'dark'
            ? (organization?.logoPublicUrlDark || organization?.logoPublicUrl)
            : (organization?.logoPublicUrl || organization?.logoPublicUrlDark));
    const previewOrgName = organization?.name || organization?.handle || 'Your organization';

    return (
        <ScrollView style={styles.container} contentInsetAdjustmentBehavior="automatic" contentContainerStyle={styles.contentContainer}>
            <View style={styles.header}>
                <Text style={styles.title}>Online Giving</Text>
                <Text style={styles.description}>Manage your giving experience and recurring gifts.</Text>
            </View>

            {tabs.length > 1 && (
                <SegmentedTabs
                    tabs={tabs}
                    value={selectedTab}
                    onChange={setActiveTab}
                    colorVariant="giving"
                    testIDPrefix="giving-tab"
                />
            )}

            {selectedTab === 'overview' && (
                <>
                    <ToggleRow label="Allow givers to choose to cover fees" description="Off: fees are always added. On: Give pages and payment forms show a Cover Fees toggle, default on. New recurring gifts retain the giver’s choice; existing schedules remain unchanged." value={!!organization?.allowGiversToChooseCoverFees} onValueChange={(value) => feeChoiceMutation.mutate(value)} disabled={!organizationID || feeChoiceMutation.isPending || !loggedInUser?.isAdmin} testID="giving-allow-fee-choice" />
                    <View style={styles.statusCard} testID="giving-management-card">
                        <View style={styles.statusTop}>
                            <View style={styles.statusIcon}>
                                <Unicon name="card-outline" size={22} colorVariant="giving" />
                            </View>
                            <View style={styles.statusTextGroup}>
                                <Text style={styles.statusEyebrow}>GIVING STATUS</Text>
                                <Text style={styles.statusHeadline}>{statusReady ? 'Ready to receive gifts' : enabled ? 'Getting set up' : 'Giving is off'}</Text>
                                <Text style={styles.rowDescription}>
                                    {statusReady ? 'Your public giving page is live and accepting gifts.' : enabled ? 'Complete onboarding to start accepting gifts.' : 'Enable giving to start accepting online gifts.'}
                                </Text>
                            </View>
                            <View style={styles.statusPill(statusReady)}>
                                <View style={styles.statusDot(statusReady)} />
                                <Text style={styles.statusPillText(statusReady)}>{statusReady ? 'LIVE' : enabled ? 'SETUP' : 'OFF'}</Text>
                            </View>
                        </View>
                        <View style={styles.statusDetail}>
                            <Text style={styles.detailLabel}>Merchant status</Text>
                            <Text style={styles.detailValue}>{merchantStatus}</Text>
                        </View>
                        <View style={styles.enableRow}>
                            <View style={styles.labelGroup}>
                                <Text style={styles.rowTitle}>Online giving</Text>
                                <Text style={styles.rowDescription}>Accept donations by card and ACH.</Text>
                            </View>
                            <Toggle testID="giving-enabled-toggle" value={enabled} onValueChange={handleToggle} disabled={isProcessing} />
                        </View>
                    </View>

                    {givingMerchant && (
                        <View style={styles.card}>
                            <View style={styles.sectionHeading}>
                                <View style={styles.sectionIcon}><Unicon name="link-outline" size={19} colorVariant="giving" /></View>
                                <View style={styles.labelGroup}>
                                    <Text style={styles.sectionTitle}>Your giving page</Text>
                                    <Text style={styles.rowDescription}>Share your page or manage your merchant account.</Text>
                                </View>
                            </View>
                            {isActive && (
                                <View style={styles.urlRow}>
                                    <Text selectable style={styles.urlText} numberOfLines={1}>
                                        {process.env.EXPO_PUBLIC_APP_WEB_URL || 'https://app.reachchms.com'}/give/{organization?.handle}
                                    </Text>
                                    <Pressable testID="copy-giving-url-button" accessibilityRole="button" accessibilityLabel="Copy public giving link" onPress={handleCopyUrl} style={styles.copyUrlButton}>
                                        <Unicon name="copy-outline" size={17} colorVariant="giving" />
                                    </Pressable>
                                </View>
                            )}
                            <View style={styles.actionsRow}>
                                <SolidButton testID="open-onboarding-button" text="Open onboarding" size="sm" iconName="open-outline" iconBefore variant="muted" type="giving" onPress={() => router.push('/settings/organization/giving/onboarding')} />
                                {isActive && loggedInUser?.can?.('View Contributions') && (
                                    <SolidButton testID="open-reporting-button" text="Open reports" size="sm" iconName="bar-chart-outline" iconBefore variant="muted" type="giving" onPress={() => router.push('/analytics/dashboards/onlinegiving')} />
                                )}
                            </View>
                        </View>
                    )}
                </>
            )}

            {canCustomizeAppearance && selectedTab === 'appearance' && (
                <View style={styles.card} testID="giving-appearance-card">
                    <View style={styles.labelGroup}>
                        <Text style={styles.sectionTitle}>Make it yours</Text>
                        <Text style={styles.rowDescription}>Choose your main color. We’ll suggest a matching background, surface, and readable text. Adjust any color afterward.</Text>
                    </View>
                    <View style={styles.appearancePreview(preview.backgroundColor)} testID="giving-appearance-preview">
                        {currentAppearance.logoPosition === 'long' && (
                            <View style={styles.previewLongLogoContainer} testID="giving-preview-long-logo">
                                {previewLogoUrl ? (
                                    <Image source={{ uri: previewLogoUrl }} style={{ width: 220, maxWidth: '100%', height: 48 }} contentFit="contain" />
                                ) : (
                                    <Text style={styles.previewLogoPlaceholder(preview.textColor)}>Add a wide logo in Organization settings</Text>
                                )}
                            </View>
                        )}
                        <View
                            testID="giving-preview-hero"
                            style={[styles.previewHero(preview.baseColor), process.env.EXPO_OS === 'web' && { backgroundImage: `linear-gradient(135deg, ${previewGradient[0]}, ${previewGradient[1]})` }]}
                        >
                            {process.env.EXPO_OS !== 'web' && (
                                <LinearGradient colors={previewGradient} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={styles.previewHeroGradient} />
                            )}
                            {currentAppearance.logoPosition !== 'long' && (
                                <View style={styles.previewIdentity}>
                                    <View style={styles.previewLogoChip(preview.surfaceColor)} testID="giving-preview-square-logo">
                                        {previewLogoUrl ? (
                                            <Image source={{ uri: previewLogoUrl }} style={{ width: 30, height: 30 }} contentFit="contain" />
                                        ) : (
                                            <Text style={styles.previewMonogram(preview.baseColor)}>{previewOrgName.trim().charAt(0).toUpperCase()}</Text>
                                        )}
                                    </View>
                                    <Text style={styles.previewOrgName(previewOnBase)} numberOfLines={2}>{previewOrgName}</Text>
                                </View>
                            )}
                            <View style={styles.previewAmountBlock}>
                                <Text style={styles.previewAmountValue(previewOnBase)}>$0.00</Text>
                                <Text style={styles.previewAmountHint(previewOnBase)}>Gift amount</Text>
                            </View>
                        </View>
                        <GivingCard title="Billing contact" surfaceColor={preview.surfaceColor} textColor={preview.textColor} style={styles.previewSurface}>
                            <View style={styles.previewField(previewInputColor)}>
                                <Text style={styles.previewFieldText(preview.textColor)}>Name</Text>
                            </View>
                            <View style={styles.previewField(previewInputColor)}>
                                <Text style={styles.previewFieldText(preview.textColor)}>Email</Text>
                            </View>
                        </GivingCard>
                        <View style={styles.previewButton(preview.baseColor)}>
                            <Text style={styles.previewButtonText(getAccessibleTextColor(preview.baseColor))}>Give now</Text>
                        </View>
                    </View>
                    <Text style={styles.previewCaption}>LIVE COLOR PREVIEW</Text>

                    <View style={styles.colorList}>
                        {[
                            { key: 'baseColor', title: 'Base color', testID: 'giving-base-color' },
                            { key: 'backgroundColor', title: 'Background color', testID: 'giving-background-color' },
                            { key: 'surfaceColor', title: 'Surface color', testID: 'giving-surface-color' },
                            { key: 'textColor', title: 'Text color', testID: 'giving-text-color' },
                        ].map(({ key, title, testID }) => (
                            <ColorSetting key={key} title={title} value={currentAppearance[key]} onChange={(value) => updateAppearance(key, value)} testID={testID} expanded={expandedColor === key} onToggle={() => setExpandedColor(expandedColor === key ? null : key)} />
                        ))}
                    </View>
                    <View style={styles.subsectionDivider} />
                    <Text style={styles.sectionTitle}>Logo options</Text>

                    <View style={styles.selectGrid}>
                        <View style={styles.selectField}>
                            <SelectInput
                                title="Logo position"
                                value={currentAppearance.logoPosition}
                                onUpdate={(value) => updateAppearance('logoPosition', value)}
                                options={[
                                    { label: 'Square logo with name', value: 'square' },
                                    { label: 'Long logo above hero', value: 'long' },
                                ]}
                                hideClearButton
                            />
                        </View>
                        <View style={styles.selectField}>
                            <SelectInput
                                title="Logo style"
                                value={currentAppearance.logoStyle}
                                onUpdate={(value) => updateAppearance('logoStyle', value)}
                                options={[
                                    { label: 'Light logo', value: 'light' },
                                    { label: 'Dark logo', value: 'dark' },
                                ]}
                                hideClearButton
                            />
                        </View>
                    </View>

                    <View style={styles.saveRow}>
                        <SolidButton
                            testID="save-giving-appearance-button"
                            text="Save appearance"
                            type="giving"
                            processing={appearanceMutation.isPending}
                            disabled={!appearanceIsValid}
                            onPress={() => appearanceMutation.mutate()}
                        />
                    </View>
                </View>
            )}

            {isActive && selectedTab === 'recurring' && (
                <View style={styles.recurringSection} testID="giving-recurring-section">
                    <Table
                        title="Recurring Giving"
                        description={loadingRecurring ? '' : `${orgRecurringGifts?.length || 0} active recurring gift${(orgRecurringGifts?.length || 0) !== 1 ? 's' : ''}`}
                        columns={recurringColumns}
                        data={orgRecurringGifts || []}
                        actions={recurringActions}
                        isLoading={loadingRecurring}
                        estimatedItemSize={64}
                        noBackground
                        ListEmptyComponent={
                            !loadingRecurring && (
                                <EmptyListPlaceholder
                                    message="No recurring gifts yet"
                                    spacing={20}
                                    size={80}
                                />
                            )
                        }
                    />
                </View>
            )}
        </ScrollView>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography, shadows, roundness }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    contentContainer: {
        padding: spacing.md,
        paddingBottom: spacing.xxl,
        gap: spacing.md,
        _web: {
            maxWidth: 840,
            alignSelf: 'center',
            width: '100%',
        },
    },
    header: {
        paddingVertical: spacing.sm,
        gap: spacing.xs,
    },
    title: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    description: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 22,
    },
    card: {
        borderRadius: roundness.lg,
        padding: spacing.lg,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        gap: spacing.md,
    },
    labelGroup: {
        flex: 1,
        gap: spacing.xs,
    },
    rowTitle: {
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    rowDescription: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        lineHeight: 18,
    },
    statusCard: {
        borderRadius: roundness.lg,
        padding: spacing.lg,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
        gap: spacing.md,
    },
    statusTop: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        gap: spacing.sm,
    },
    statusIcon: {
        width: 44,
        height: 44,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.md,
        backgroundColor: colors.givingContainer,
    },
    statusTextGroup: {
        flex: 1,
        minWidth: 150,
        gap: spacing.xs,
    },
    statusEyebrow: {
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        color: colors.giving,
        letterSpacing: 1,
    },
    statusHeadline: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    statusPill: (active) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs,
        borderRadius: roundness.xxl,
        backgroundColor: active ? colors.givingContainer : colors.surfaceVariant,
    }),
    statusDot: (active) => ({
        width: 7,
        height: 7,
        borderRadius: 4,
        backgroundColor: active ? colors.giving : colors.textSecondary,
    }),
    statusPillText: (active) => ({
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        color: active ? colors.giving : colors.textSecondary,
    }),
    statusDetail: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: spacing.xs,
        paddingVertical: spacing.md,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: colors.borderSubtle,
    },
    detailLabel: {
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    detailValue: {
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color: colors.text,
        textTransform: 'capitalize',
    },
    enableRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    sectionHeading: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    sectionIcon: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.sm,
        backgroundColor: colors.givingContainer,
    },
    sectionTitle: {
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    urlRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
        paddingLeft: spacing.sm,
        paddingRight: spacing.xs,
        borderRadius: roundness.sm,
        backgroundColor: colors.background,
    },
    urlText: {
        flex: 1,
        minWidth: 0,
        paddingVertical: spacing.sm,
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color: colors.text,
    },
    copyUrlButton: {
        width: 32,
        height: 32,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: roundness.sm,
        _web: {
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            _hover: {
                backgroundColor: colors.textSecondaryContainer,
            },
        },
    },
    actionsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: spacing.xs,
    },
    appearancePreview: (backgroundColor) => ({
        width: '100%',
        maxWidth: 440,
        alignSelf: 'center',
        padding: spacing.md,
        gap: spacing.sm,
        borderRadius: roundness.md,
        backgroundColor,
    }),
    previewLongLogoContainer: {
        minHeight: 48,
        alignItems: 'center',
        justifyContent: 'center',
    },
    previewLogoPlaceholder: (color) => ({
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color,
        textAlign: 'center',
        opacity: 0.65,
    }),
    previewHero: (backgroundColor) => ({
        padding: spacing.md,
        gap: spacing.sm,
        borderRadius: roundness.lg,
        overflow: 'hidden',
        backgroundColor,
    }),
    previewHeroGradient: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    previewIdentity: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.sm,
    },
    previewLogoChip: (backgroundColor) => ({
        width: 36,
        height: 36,
        borderRadius: roundness.sm,
        backgroundColor,
        alignItems: 'center',
        justifyContent: 'center',
    }),
    previewMonogram: (color) => ({
        fontSize: typography.h2,
        fontFamily: fonts.heading,
        color,
    }),
    previewOrgName: (color) => ({
        flex: 1,
        fontSize: typography.body,
        fontFamily: fonts.heading,
        color,
    }),
    previewAmountBlock: {
        alignItems: 'center',
        paddingVertical: spacing.sm,
        gap: spacing.xs,
    },
    previewAmountValue: (color) => ({
        fontSize: 30,
        fontFamily: fonts.heading,
        color,
    }),
    previewAmountHint: (color) => ({
        fontSize: typography.micro,
        fontFamily: fonts.body,
        color,
        opacity: 0.75,
    }),
    previewSurface: {
        padding: spacing.sm,
        gap: spacing.xs,
    },
    previewField: (backgroundColor) => ({
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.sm,
        borderRadius: roundness.sm,
        backgroundColor,
    }),
    previewFieldText: (color) => ({
        fontSize: typography.caption,
        fontFamily: fonts.body,
        color,
        opacity: 0.6,
    }),
    previewButton: (backgroundColor) => ({
        alignItems: 'center',
        padding: spacing.sm,
        borderRadius: roundness.sm,
        backgroundColor,
    }),
    previewButtonText: (color) => ({
        fontSize: typography.caption,
        fontFamily: fonts.bold,
        color,
    }),
    previewCaption: {
        textAlign: 'center',
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        letterSpacing: 1,
    },
    colorList: {
        gap: spacing.md,
    },
    colorSetting: {
        width: '100%',
        minWidth: 0,
        gap: spacing.xs,
    },
    subsectionDivider: {
        height: 1,
        backgroundColor: colors.borderSubtle,
    },
    inputLabel: {
        fontSize: typography.caption,
        fontFamily: fonts.heading,
        color: colors.textSecondary,
    },
    colorInputRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.xs,
    },
    colorSwatch: (backgroundColor) => ({
        width: 44,
        height: 44,
        borderRadius: roundness.sm,
        backgroundColor,
        borderWidth: 1,
        borderColor: colors.border,
        _web: {
            cursor: 'pointer',
        },
    }),
    colorTextInput: {
        flex: 1,
        minWidth: 0,
        height: 44,
        paddingHorizontal: spacing.sm,
        borderRadius: roundness.sm,
        backgroundColor: colors.inputBackground,
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: typography.body,
        borderWidth: 1,
        borderColor: 'transparent',
    },
    invalidColorInput: {
        borderColor: colors.error,
    },
    colorPicker: {
        width: '100%',
        minWidth: 0,
        gap: spacing.sm,
        paddingVertical: spacing.xs,
    },
    colorPanel: {
        height: 150,
        borderRadius: roundness.sm,
    },
    hueSlider: {
        height: 24,
        borderRadius: roundness.xxl,
    },
    selectGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: spacing.md,
    },
    selectField: {
        minWidth: 0,
        flexGrow: 1,
        flexBasis: 220,
    },
    saveRow: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
    },
    recurringSection: {
        borderRadius: roundness.md,
        overflow: 'hidden',
        minHeight: 300,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
    },
    statusBadge: {
        paddingHorizontal: spacing.sm,
        paddingVertical: spacing.xs,
        borderRadius: roundness.sm,
        alignSelf: 'flex-start',
    },
    statusText: {
        fontSize: typography.micro,
        fontFamily: fonts.bold,
        textTransform: 'uppercase',
    },
}));
